import { getActions } from './common/common.actions';
import { CommonState, initialState } from '@redux/common/common.state';
import { Injectable } from '@angular/core';
import { CommonEffects } from '@redux/common/common.effects';
import { getReducer } from '@redux/common/common.reducer';
import { getSelectors } from '@redux/common/common.selector';
import { User } from '@models/account/user.model';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, Observable, of, take } from 'rxjs';
import { Action, createAction, createSelector, on, props } from '@ngrx/store';
import { authenticationActions } from '@redux/authentication.redux';
import { Media } from '@models/common/media.model';
import { get } from 'lodash-es';

export const userStateKey = 'UserState';

export interface UserState extends CommonState<User> {
  avatars?: { [key: string]: string };
}

export const userState: UserState = { ...initialState<User>(), loading: true, avatars: {} };
//
export const userActions = {
  ...getActions<User>(userStateKey),
  deleteAccount: createAction(`[${userStateKey}]DeleteAccount`, props<{ user: User | null }>()),
  deleteAccountSuccess: createAction(
    `[${userStateKey}]DeleteAccountSuccess`,
    props<{ user: User | null }>(),
  ),
  updateUserFlag: createAction(
    `[${userStateKey}]UpdateUserFlag`,
    props<{
      user: User | null;
      flag: string;
      value: boolean;
    }>(),
  ),
  updateUserFlagSuccess: createAction(
    `[${userStateKey}]UpdateUserFlagSuccess`,
    props<{ user: User | null }>(),
  ),
  //
  avatar: createAction(`[${userStateKey}]Avatar`, props<{ userId: string }>()),
  avatarSuccess: createAction(
    `[${userStateKey}]AvatarSuccess`,
    props<{ userId: string; avatarUrl: string }>(),
  ),
  avatarFail: createAction(
    `[${userStateKey}]AvatarFail`,
    props<{ userId: string; error: string }>(),
  ),
};
//
export const selectUserBase = getSelectors<User>(userStateKey);
export const selectUser = {
  ...selectUserBase,
  avatars: createSelector(selectUserBase.selectFeature, (state) => {
    return get(state, 'avatars', <{ [key: string]: string }>{}) as { [key: string]: string };
  }),
};

export const userReducers = getReducer<User>(userState, userActions, [
  on(userActions.avatarSuccess, (state: UserState, { userId, avatarUrl }) => ({
    ...state,
    avatars: { ...state.avatars, [userId]: avatarUrl }, // Actualizamos el avatar en el estado
  })),
  on(userActions.avatarFail, (state: UserState, { userId }) => ({
    ...state,
    avatars: { ...state.avatars, [userId]: 'https://dummyimage.com/200/ebebeb/bdbdbd.jpg?text=Unavailable' }, // En caso de fallo, utilizamos un placeholder
  })),
]);

@Injectable()
export class UserEffects extends CommonEffects<User> {
  //
  private pendingRequests = new Set<string>(); // Set para manejar solicitudes en proceso

  constructor() {
    super();
    Object.assign(this, this.createEffects(userActions, selectUser));
  }

  deleteAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(userActions.deleteAccount),
      mergeMap(({ user }) => {
        return this.api.put<User>(`users/archive/${user?.id}`).pipe(
          map(() => {
            localStorage.removeItem('token');
            return authenticationActions.logoutSuccess();
          }),
          catchError((error) => of(userActions.updateFail({ error: error.message }))),
        );
      }),
    );
  });

  updateUserFlag$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(userActions.updateUserFlag),
      mergeMap(({ user, flag, value }) => {
        return this.api
          .post<User>(`users/flag/update-or-create-flag`, {
            userId: user?.id,
            name: flag,
            value: value,
          })
          .pipe(
            map((model) => {
              return userActions.updateUserFlagSuccess({ user: model });
            }),
            catchError((error) => of(userActions.updateFail({ error: error.message }))),
          );
      }),
    );
  });

  fetchAvatar$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(userActions.avatar),
      mergeMap(({ userId }) => {
        if (this.pendingRequests.has(userId)) {
          // Si ya hay una solicitud en proceso para este userId, no hacemos otra
          return of();
        }

        // Añadir userId a las solicitudes en proceso
        this.pendingRequests.add(userId);

        return this.store.select(selectUser.avatars).pipe(
          take(1),
          mergeMap((avatars) => {
            // Si el avatar ya está en el store, devolvemos el éxito sin hacer el request
            if (avatars[userId]) {
              this.pendingRequests.delete(userId); // Eliminar de las solicitudes en proceso
              return of(userActions.avatarSuccess({ userId, avatarUrl: avatars[userId] }));
            }

            // Hacemos la solicitud al API si no está en el store
            return this.api.get<{ media: Media }>(`users/${userId}/get-profile-media`).pipe(
              map((response) => {
                const avatarUrl =
                  response.media.url || 'https://dummyimage.com/200/ebebeb/bdbdbd.jpg?text=Unavailable';
                this.pendingRequests.delete(userId); // Eliminar de las solicitudes en proceso
                return userActions.avatarSuccess({ userId, avatarUrl });
              }),
              catchError((error) => {
                this.pendingRequests.delete(userId); // Eliminar de las solicitudes en proceso
                return of(userActions.avatarFail({ userId, error: error.message }));
              }),
            );
          }),
        );
      }),
    );
  });
}
